// ┌─────────────────────────────────────────────────────────────────────────┐
// │ MOTOR DE PROGRAMES (pas 7 — Càlculs) — GENÈRIC, sense programes de client. │
// └─────────────────────────────────────────────────────────────────────────┘
//
// Aquest fitxer ja NO registra cap programa concret. Els programes (i les seves
// dependències) viuen al catàleg de Sherpa (taules `transform_program` +
// `transform_program_dependency`) i el store els carrega en runtime:
//   loadPrograms() → fetchProgramsByAliases() → instantiateRemoteProgram()
// (veure stores/computedStore.js i core/computed/remoteLoader.js).
//
// Cada programa és un mòdul pur `create(ctx)` que rep TOT el que necessita per
// `ctx.deps` (codi + datasets baixats de BD). El component no sap res de cap
// camp ni de cap client: només sap executar la forma { proposals, reports } que
// torna qualsevol programa.
//
// Per AFEGIR un programa nou: NO es toca aquest fitxer — es dóna d'alta el
// programa (i les seves dependències) al catàleg via la secció «Programes».
import { programsForFields } from '@vft/config/processors/programBindings.js';

// Sense fallback estàtic: la FONT única són els programes del model. El store
// passa SEMPRE la llista efectiva de calculadors (instanciats des de BD) per
// `options.calculators`; aquesta constant buida només manté la compatibilitat
// dels consumidors que la importen (defaultEnabled, fallbacks de getters).
export const COMPUTED_CALCULATORS = Object.freeze([]);

export async function runCalculators(outputRows, fields, options = {}) {
  const enabled = options.enabled || {};
  const config = options.config || {};
  // Llista de calculadors a executar. Per defecte els estàtics del component;
  // el store hi passa la llista EFECTIVA (calculador del model on n'hi ha, estàtic
  // de fallback on no) perquè el `code` del catàleg s'executi quan carrega net.
  const calculators = options.calculators || COMPUTED_CALCULATORS;
  const proposals = [];
  const reports = {};
  // Programes a executar segons els camps presents (binding camp→programa).
  const triggered = new Set(programsForFields(fields));
  for (const calc of calculators) {
    if (!triggered.has(calc.id)) continue;
    if (enabled[calc.id] === false) continue;
    const calcConfig = config[calc.id];
    if (typeof calc.prepare === 'function') await calc.prepare();
    const raw = calc.run(outputRows, fields, calcConfig);
    // Una calculadora pot tornar un array de propostes (forma antiga) o
    // un objecte { proposals, ...extra } per adjuntar informacio addicional
    // (per exemple, files descartades).
    if (Array.isArray(raw)) {
      for (const p of raw) proposals.push(p);
    } else if (raw && typeof raw === 'object') {
      if (Array.isArray(raw.proposals)) for (const p of raw.proposals) proposals.push(p);
      const { proposals: _omit, ...extra } = raw;
      if (Object.keys(extra).length > 0) reports[calc.id] = extra;
    }
  }
  return { proposals, reports };
}

// ── EXECUCIÓ TROCEJADA (streaming) ──────────────────────────────────────────
// El motor del pas 7 ha de ser DINÀMIC: amb fitxers grans (desenes de milers de
// files) un `run()` atòmic bloquejaria el navegador i no donaria feedback. Per
// això s'executa per LOTS, cedint el control al navegador entre lots perquè la UI
// repinti (consola en viu + barra de progrés) i no es congeli.
//
// El contracte `run(outputRows, fields, config)` NO canvia: rep un subconjunt de
// files i torna { proposals, [rows], [report], ...arraysCrus }. El `rowIndex` que
// torna és RELATIU al lot → el motor el reindexga sumant-li l'offset del lot.
//
// REPORTS AGREGATS: alguns programes construeixen al `run()` un `report` agregant
// TOTES les files (agrupacions, comptadors). Trocejar i fusionar aquests reports
// seria incorrecte. Dos camins:
//   · El programa exposa `buildReport(rawArrays, fields, config)` → el motor acumula
//     les dades crues per lot i crida buildReport UN cop al final (progrés REAL).
//   · Si NO l'exposa i el `run()` emet un report agregat → el motor l'executa ATÒMIC
//     (un sol lot amb totes les files) per no trencar l'agregació (sense progrés
//     per lot, però correcte). Migrar el programa a buildReport és la via per al progrés.

// Lot prou petit perquè CADA tros síncron sigui curt (ms, no segons) i el
// navegador no es congeli amb programes pesats (els que fan, p.ex., matching
// contra un dataset gran per fila). Com més petit, més fluid el progrés i més
// sovint es cedeix el control; el sobrecost dels yields és menor.
export const DEFAULT_BATCH_SIZE = 300;

// Cedeix el control al navegador (macrotask) perquè Vue repinti el log/progrés i
// l'event loop respiri entre lots. setTimeout(0) funciona també en segon pla
// (a diferència de requestAnimationFrame, que es pausa si la pestanya no és visible).
export function yieldToBrowser() {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

function reindexProposals(list, offset) {
  if (!offset) return list;
  for (const p of list) p.rowIndex += offset;
  return list;
}

// Executa UNA calculadora sobre `outputRows` de forma trocejada amb feedback.
// opts: { config, onEvent(evt), batchSize, shouldStop() }.
// Retorna { proposals, report, transformedRows, raw }.
//   · transformedRows: si el programa transforma files (preprocés torna `rows`),
//     són TOTES les files transformades (concat per lot); si no, null.
//   · raw: arrays crus acumulats (discarded, flagged…) per a buildReport o report genèric.
export async function runOneCalculator(calc, outputRows, fields, opts = {}) {
  const cfg = opts.config;
  const onEvent = typeof opts.onEvent === 'function' ? opts.onEvent : () => {};
  const batchSize = opts.batchSize || DEFAULT_BATCH_SIZE;
  const shouldStop = typeof opts.shouldStop === 'function' ? opts.shouldStop : () => false;

  const headers = outputRows.headers;
  const allRows = outputRows.rows || [];
  const total = allRows.length;

  const proposals = [];
  const raw = {};            // arrays crus per clau (discarded, flagged…)
  let transformedRows = null; // si el programa torna `rows`
  let legacyReport = null;    // report emès directament pel run() (modes no-buildReport)

  // Acumula el resultat cru d'un lot al conjunt total, reindexant els rowIndex.
  const ingest = (result, offset) => {
    if (Array.isArray(result)) {
      reindexProposals(result, offset);
      for (const p of result) proposals.push(p);
      return;
    }
    if (!result || typeof result !== 'object') return;
    if (Array.isArray(result.proposals)) {
      reindexProposals(result.proposals, offset);
      for (const p of result.proposals) proposals.push(p);
    }
    if (Array.isArray(result.rows)) {
      if (transformedRows == null) transformedRows = [];
      for (const r of result.rows) transformedRows.push(r);
    }
    if (result.report) legacyReport = result.report;
    for (const key of Object.keys(result)) {
      if (key === 'proposals' || key === 'rows' || key === 'report' || key === 'headers') continue;
      const v = result[key];
      if (!Array.isArray(v)) continue;
      if (!raw[key]) raw[key] = [];
      // Els ítems crus solen portar rowIndex (p.ex. discarded) → reindexar.
      for (const it of v) {
        if (it && typeof it === 'object' && typeof it.rowIndex === 'number' && offset) {
          raw[key].push({ ...it, rowIndex: it.rowIndex + offset });
        } else {
          raw[key].push(it);
        }
      }
    }
  };

  const streamable = typeof calc.buildReport === 'function';
  const runBatch = (from, to) => calc.run({ headers, rows: allRows.slice(from, to) }, fields, cfg);

  if (typeof calc.prepare === 'function') await calc.prepare();

  if (streamable) {
    // Programa preparat per a stream: trocejar sempre i construir el report al final.
    if (total === 0) {
      ingest(calc.run({ headers, rows: [] }, fields, cfg), 0);
    }
    for (let off = 0; off < total; off += batchSize) {
      if (shouldStop()) break;
      ingest(runBatch(off, off + batchSize), off);
      onEvent({ type: 'batch', calcId: calc.id, processed: Math.min(off + batchSize, total), total });
      await yieldToBrowser();
    }
    const report = calc.buildReport(raw, fields, cfg) || null;
    return { proposals, report, transformedRows, raw };
  }

  // Programa NO preparat per a stream. Si cap en un lot, execució directa.
  if (total <= batchSize) {
    ingest(runBatch(0, total), 0);
    onEvent({ type: 'batch', calcId: calc.id, processed: total, total });
    return { proposals, report: legacyReport, transformedRows, raw };
  }

  // Múltiples lots: sondegem el primer per detectar si emet report agregat.
  const firstResult = runBatch(0, batchSize);
  const emitsAggregatedReport = firstResult && !Array.isArray(firstResult)
    && firstResult.report && Array.isArray(firstResult.report.sections)
    && firstResult.report.sections.length > 0;

  if (emitsAggregatedReport) {
    // No es pot trocejar sense trencar l'agregació → execució atòmica (totes les files).
    onEvent({ type: 'atomic', calcId: calc.id, total });
    await yieldToBrowser();
    ingest(calc.run(outputRows, fields, cfg), 0);
    onEvent({ type: 'batch', calcId: calc.id, processed: total, total });
    return { proposals, report: legacyReport, transformedRows, raw };
  }

  // Troceable (sense report agregat): acumulem el primer lot ja calculat i seguim.
  ingest(firstResult, 0);
  onEvent({ type: 'batch', calcId: calc.id, processed: Math.min(batchSize, total), total });
  await yieldToBrowser();
  for (let off = batchSize; off < total; off += batchSize) {
    if (shouldStop()) break;
    ingest(runBatch(off, off + batchSize), off);
    onEvent({ type: 'batch', calcId: calc.id, processed: Math.min(off + batchSize, total), total });
    await yieldToBrowser();
  }
  return { proposals, report: legacyReport, transformedRows, raw };
}

export function applyComputedProposals(outputRows, proposals, rejected = {}) {
  const rows = outputRows.rows.map((r) => [...r]);
  const reasonByField = new Map();
  for (const p of proposals) {
    const key = `${p.rowIndex}:${p.calculator}:${p.fieldId}`;
    if (rejected[key]) continue;
    if (p.fieldIndex == null || p.fieldIndex < 0) continue;
    rows[p.rowIndex][p.fieldIndex] = p.proposedValue;
    let m = reasonByField.get(p.fieldId);
    if (!m) {
      m = new Map();
      reasonByField.set(p.fieldId, m);
    }
    m.set(p.rowIndex, p.reason || '');
  }

  if (reasonByField.size === 0) {
    return { headers: outputRows.headers, rows };
  }

  const motiveFieldIds = [...reasonByField.keys()];
  const motiveHeaders = motiveFieldIds.map((fid) => `${fid}__motiu`);
  const newHeaders = [...outputRows.headers, ...motiveHeaders];
  const newRows = rows.map((row, rIdx) => {
    const extras = motiveFieldIds.map((fid) => reasonByField.get(fid).get(rIdx) ?? '');
    return [...row, ...extras];
  });
  return { headers: newHeaders, rows: newRows };
}
